// Agent IA v2 (back/docs/agent-v2/SPEC.md §4, §8): API types, normalisation, French labels.
// Pure module (no Angular import) so the panel state can be tested with node:test.
import { ApprovalAction, normalizeApprovalAction } from './agent-action-card';

export type AgentRunStatus = 'queued' | 'running' | 'waiting_user' | 'completed' | 'failed' | 'cancelled';
export type AgentIngestionStatus = 'pending' | 'processing' | 'ready' | 'failed';
// executing, partially_executed, expired, withdrawn: proposals of phase 3 (back/docs/mcp/03-APPROBATION.md §4).
export type AgentActionStatus = 'pending' | 'approved' | 'executing' | 'rejected' | 'executed' | 'partially_executed' | 'failed' | 'expired' | 'withdrawn';
const ACTION_STATUSES: AgentActionStatus[] = ['pending', 'approved', 'executing', 'rejected', 'executed', 'partially_executed', 'failed', 'expired', 'withdrawn'];
export type AgentChangesetStatus = 'applied' | 'undone';
export type AgentStepStatus = 'running' | 'ok' | 'failed';

export interface AgentScope { projectId?: string; dpgfId?: string; }
export interface AgentContext extends AgentScope { planId?: string; }
// lastRun: state of the conversation's last run in the history list (GET /conversations), absent if unknown.
// source: 'mcp' for the journal of an AI connection (Claude, docs/mcp/06-FRONT.md §7): read-only, absent for the agent's own.
export interface AgentRunState { id: string; status: AgentRunStatus; cancelRequested: boolean; }
export interface AgentConversation { id: string; title: string; scope: AgentScope; projectTitle?: string; createdAt: string; updatedAt: string; lastRun?: AgentRunState | null; source?: 'mcp'; }
export interface AgentMessage { id: string; role: 'user' | 'assistant'; content: string; fileIds: string[]; runId?: string; createdAt: string; }
export interface AgentQuestion { id: string; text: string; options: string[]; multiple?: boolean; }
export interface AgentRunError { code: string; message: string; }
export interface AgentRun {
  id: string; status: AgentRunStatus; cancelRequested: boolean;
  question: AgentQuestion | null; error: AgentRunError | null; summary: string;
  lastText: string; // latest assistant_text of the run (progress only, never stored as a message)
}
export interface AgentFile { id: string; name: string; mimeType: string; size: number; status: AgentIngestionStatus; error: string; }
// Optional fields, only when the API gives them (proposals of an AI connection, 03-APPROBATION §4, §6): source, family,
// expiry, alerts (never folded), effects and irreversibility, all computed by the server.
export interface AgentAction {
  id: string; type: string; title: string; description: string; targetLabel: string; status: AgentActionStatus; error: string; createdAt: string;
  source?: 'mcp'; family?: string; expiresAt?: string; alerts?: { code: string; message: string }[]; effects?: string[]; irreversible?: boolean;
}
// action: target.action of the changeset when it has one ('attach' = plans attached from a PDF of the conversation,
// 'reject' = AI measurement proposals withdrawn by the agent).
export interface AgentChangeset { id: string; kind: string; action?: string; summary: string; status: AgentChangesetStatus; createdAt: string; }
export interface AgentEvent { seq: number; type: string; runId: string; data: Record<string, any>; createdAt: string; }
export interface AgentStep { seq: number; tool: string; label: string; status: AgentStepStatus; summary: string; }

// Raw payloads of ${api.base}/ai/agent/v2 (SPEC §4). Identifiers may come as `id` or `_id`.
export type RawRecord = Record<string, any>;
export interface AgentSnapshotResponse {
  conversation: RawRecord; messages?: RawRecord[]; run?: RawRecord | null; pendingActions?: RawRecord[];
  changesets?: RawRecord[]; files?: RawRecord[]; lastSeq?: number;
}
export interface AgentCreateBody { title?: string; projectId?: string; dpgfId?: string; }
export interface AgentSendBody { content: string; fileIds: string[]; requestId: string; }

export interface AgentApi {
  list(signal?: AbortSignal): Promise<{ conversations: RawRecord[] }>;
  create(body: AgentCreateBody): Promise<{ conversation: RawRecord }>;
  get(conversationId: string, signal?: AbortSignal): Promise<AgentSnapshotResponse>;
  events(conversationId: string, after: number, limit: number, signal?: AbortSignal): Promise<{ events: RawRecord[]; lastSeq?: number }>;
  send(conversationId: string, body: AgentSendBody): Promise<{ message?: RawRecord; run?: RawRecord | null }>;
  cancel(runId: string): Promise<{ run: RawRecord }>;
  approve(actionId: string): Promise<{ action: RawRecord }>;
  reject(actionId: string): Promise<{ action: RawRecord }>;
  undo(changesetId: string): Promise<{ changeset: RawRecord }>;
  // « À valider » pastille: GET /actions?status=pending (03-APPROBATION §7.1); absent on older adapters.
  pendingApprovals?(signal?: AbortSignal): Promise<unknown>;
  upload(conversationId: string, files: File[]): Promise<{ files: RawRecord[] }>;
  transcribe?(conversationId: string, audio: Blob, signal?: AbortSignal): Promise<{ text: string }>;
}

// Mirror of LIMITS / UPLOAD_TYPES in back/shared/agent-v2/constants.js (checked by the tests).
export const AGENT_LIMITS = Object.freeze({ messageChars: 16000, titleChars: 120, filesPerUpload: 10, fileBytes: 50 * 1024 * 1024, filesPerConversation: 60 });
export const AGENT_UPLOAD_EXTENSIONS = Object.freeze(['.pdf', '.png', '.jpg', '.jpeg', '.docx', '.xlsx', '.csv', '.txt']);
export const AGENT_UPLOAD_ACCEPT = AGENT_UPLOAD_EXTENSIONS.join(',');

const RUN_STATUSES: AgentRunStatus[] = ['queued', 'running', 'waiting_user', 'completed', 'failed', 'cancelled'];
const ACTIVE_RUN_STATUSES: AgentRunStatus[] = ['queued', 'running'];
const FINAL_RUN_STATUSES: AgentRunStatus[] = ['completed', 'failed', 'cancelled'];
export const isActiveRun = (run: AgentRun | null | undefined) => Boolean(run && ACTIVE_RUN_STATUSES.includes(run.status));
export const isFinalRun = (run: AgentRun | null | undefined) => Boolean(run && FINAL_RUN_STATUSES.includes(run.status));
export const isReadingFile = (file: AgentFile | undefined) => file?.status === 'pending' || file?.status === 'processing';

export const RUN_STATUS_LABELS: Record<AgentRunStatus | 'idle' | 'cancelling', string> = {
  idle: 'Prêt à vous aider', queued: 'Démarrage de la tâche', running: 'En cours', cancelling: 'Arrêt en cours',
  waiting_user: 'Votre réponse est attendue', completed: 'Terminé', failed: 'La tâche n’a pas abouti', cancelled: 'Arrêté'
};
export const FILE_STATUS_LABELS: Record<AgentIngestionStatus, string> = {
  pending: 'Lecture en attente', processing: 'Lecture en cours', ready: 'Lu', failed: 'Lecture impossible'
};
export const ACTION_TYPE_LABELS: Record<string, string> = {
  deletePost: 'Supprimer un poste', deleteSection: 'Supprimer une section', deleteLot: 'Supprimer un lot',
  deleteLibraryPost: 'Supprimer un article de la bibliothèque', sendDce: 'Envoyer le DCE', inviteCompany: 'Inviter une entreprise'
};
export const ACTION_STATUS_LABELS: Record<AgentActionStatus, string> = {
  pending: 'À valider', approved: 'Validée, exécution en cours', executing: 'Exécution en cours', rejected: 'Refusée', executed: 'Effectuée',
  partially_executed: 'Effectuée en partie', failed: 'Non effectuée', expired: 'Expirée', withdrawn: 'Retirée'
};
// Kinds of the AI connections' changesets (02-OUTILS-A/B, 03-APPROBATION §12.1) besides the agent's own.
export const CHANGESET_KIND_LABELS: Record<string, string> = {
  dpgf: 'DPGF', library: 'Bibliothèque', plan: 'Métrés sur plan', project: 'Projet et DPGF', projectUpdate: 'Projet', task: 'Tâches',
  addressBook: 'Carnet d’adresses', consultation: 'Consultation', dce: 'DCE', agency: 'Agence'
};
const TOOL_LABELS: Record<string, string> = {
  create_project: 'Création du projet et de sa DPGF',
  run_script: 'Travail sur la DPGF', read_document: 'Lecture d’un document', search_documents: 'Recherche dans les documents',
  search_library: 'Recherche dans la bibliothèque', search_cstb: 'Recherche dans le CSTB',
  read_cstb_post: 'Lecture d’un CCTP CSTB', read_cstb_lot_generalites: 'Lecture des généralités CSTB',
  measure_plan: 'Métrés sur plan', inspect_plan: 'Lecture de plan', ask_user: 'Question', propose_action: 'Proposition d’action', finish: 'Finalisation'
};
const FILE_ERROR_LABELS: Record<string, string> = {
  UNSUPPORTED_TYPE: 'Format non pris en charge.', ENCRYPTED: 'Document protégé par un mot de passe.', ZIP_BOMB: 'Fichier compressé refusé par sécurité.',
  TOO_LARGE: 'Fichier trop volumineux.', OCR_FAILED: 'La lecture du document a échoué.', CORRUPTED: 'Fichier endommagé.', EMPTY: 'Aucun texte lisible.'
};
const RUN_ERROR_LABELS: Record<string, string> = {
  BUDGET_EXCEEDED: 'La tâche a atteint son budget. Découpez la demande en étapes plus courtes.',
  LIMIT_ITERATIONS: 'La tâche a atteint son nombre maximal d’étapes. Précisez ou découpez la demande.',
  TOO_MANY_ATTEMPTS: 'La tâche a été interrompue plusieurs fois. Relancez-la dans un instant.',
  QUOTA_EXCEEDED: 'La limite d’utilisation de l’agent est atteinte pour aujourd’hui.',
  LLM_TIMEOUT: 'Le service d’IA n’a pas répondu à temps. Réessayez dans un instant.',
  LLM_RATE_LIMIT: 'Le service d’IA est très sollicité. Réessayez dans un instant.',
  LLM_UNAVAILABLE: 'Le service d’IA est momentanément indisponible. Réessayez dans un instant.'
};

// ---- Normalisation ------------------------------------------------------------------------

const str = (value: unknown, max = 100000) => typeof value === 'string' ? value.slice(0, max) : value == null ? '' : String(value).slice(0, max);
// One display line: no control, zero-width or bidirectional formatting characters, collapsed spaces.
const cleanText = (value: unknown, max = 1000) => typeof value === 'string'
  ? value.slice(0, max * 2).replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2060-\u2069\ufeff]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max) : '';
const idOf = (raw: RawRecord | null | undefined, ...keys: string[]) => {
  for (const key of [...keys, 'id', '_id']) if (raw?.[key] != null && raw[key] !== '') return str(raw[key], 200);
  return '';
};
const dateOf = (value: unknown) => value instanceof Date ? value.toISOString() : str(value, 40);
const pick = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T => allowed.includes(value as T) ? value as T : fallback;
// Server messages are plain text; error codes are never shown alone.
const errorText = (value: unknown, labels: Record<string, string>, fallback: string) => {
  if (typeof value === 'string') return labels[value] || value.slice(0, 300) || fallback;
  const code = str((value as RawRecord)?.code, 60), message = str((value as RawRecord)?.message, 300);
  return labels[code] || message || fallback;
};

export function normalizeConversation(raw: RawRecord): AgentConversation {
  const scope: AgentScope = {};
  if (raw?.scope?.projectId) scope.projectId = str(raw.scope.projectId, 40);
  if (raw?.scope?.dpgfId) scope.dpgfId = str(raw.scope.dpgfId, 40);
  const conversation: AgentConversation = { id: idOf(raw), title: str(raw?.title, AGENT_LIMITS.titleChars), scope, createdAt: dateOf(raw?.createdAt), updatedAt: dateOf(raw?.updatedAt || raw?.createdAt) };
  if (typeof raw?.projectTitle === 'string') conversation.projectTitle = str(raw.projectTitle, AGENT_LIMITS.titleChars);
  if (raw && 'lastRun' in raw) conversation.lastRun = normalizeRunState(raw.lastRun);
  if (raw?.source === 'mcp') conversation.source = 'mcp';
  return conversation;
}
// Author of an AI connection's journal (docs/mcp/06-FRONT.md §1 U14, §7): « Claude » for a verified client, otherwise
// « Connexion IA ». The API titles these conversations « <author> — <project> » from the connection's author tag
// (back shared mcpRun.conversationTitle); null for the agent's own conversations.
export interface ConversationAuthor { badge: string; changes: string; changesLabel: string; readOnly: string; }
const CONVERSATION_AUTHORS: Record<'claude' | 'connection', ConversationAuthor> = Object.freeze({
  claude: Object.freeze({ badge: 'Claude', changes: 'Modifications de Claude', changesLabel: 'Modifications faites par Claude',
    readOnly: 'Cette conversation retrace le travail de Claude ; ouvrez une nouvelle conversation pour l’Agent IA.' }),
  connection: Object.freeze({ badge: 'Connexion IA', changes: 'Modifications de la connexion IA', changesLabel: 'Modifications faites par la connexion IA',
    readOnly: 'Cette conversation retrace le travail de la connexion IA ; ouvrez une nouvelle conversation pour l’Agent IA.' })
});
export function conversationAuthor(conversation: Pick<AgentConversation, 'source' | 'title'> | null | undefined): ConversationAuthor | null {
  if (conversation?.source !== 'mcp') return null;
  return CONVERSATION_AUTHORS[/^Connexion IA(?:\s|$)/.test(conversation.title || '') ? 'connection' : 'claude'];
}
export function normalizeRunState(raw: RawRecord | null | undefined): AgentRunState | null {
  const id = idOf(raw, 'runId');
  return id && RUN_STATUSES.includes(raw?.status) ? { id, status: raw!.status, cancelRequested: raw!.cancelRequested === true } : null;
}
export function normalizeMessage(raw: RawRecord): AgentMessage {
  const message: AgentMessage = {
    id: idOf(raw), role: raw?.role === 'user' ? 'user' : 'assistant', content: str(raw?.content),
    fileIds: Array.isArray(raw?.fileIds) ? raw.fileIds.map((id: unknown) => str(id, 200)).filter(Boolean) : [], createdAt: dateOf(raw?.createdAt)
  };
  if (raw?.runId) message.runId = str(raw.runId, 200);
  return message;
}
export function normalizeQuestion(raw: RawRecord | null | undefined): AgentQuestion | null {
  if (!raw) return null;
  const text = str(raw.text ?? raw.question, 4000);
  const options = Array.isArray(raw.options) ? [...new Set(raw.options.map((option: unknown) => str(option, 500).trim()).filter(Boolean))].slice(0, 20) : [];
  return text ? { id: idOf(raw, 'questionId'), text, options, ...(raw.multiple === true ? { multiple: true } : {}) } : null;
}
export function normalizeRun(raw: RawRecord): AgentRun {
  const error = raw?.error ? { code: str(raw.error.code, 60), message: str(raw.error.message, 500) } : null;
  const status = pick(raw?.status, RUN_STATUSES, 'queued');
  return {
    id: idOf(raw, 'runId'), status, cancelRequested: raw?.cancelRequested === true,
    question: status === 'waiting_user' ? normalizeQuestion(raw?.question) : null, error, summary: str(raw?.summary, 20000), lastText: ''
  };
}
export function normalizeFile(raw: RawRecord): AgentFile {
  const ingestion = raw?.ingestion || {};
  return {
    id: idOf(raw, 'fileId'), name: str(raw?.name, 300), mimeType: str(raw?.mimeType, 200), size: Number(raw?.size) || 0,
    status: pick(ingestion.status ?? raw?.status, ['pending', 'processing', 'ready', 'failed'] as AgentIngestionStatus[], 'pending'),
    error: ingestion.error ? errorText(ingestion.error, FILE_ERROR_LABELS, 'Lecture impossible.') : ''
  };
}
export function normalizeAction(raw: RawRecord): AgentAction {
  const preview = raw?.preview || {};
  const render = raw?.render && typeof raw.render === 'object' ? raw.render : {};
  const action: AgentAction = {
    id: idOf(raw, 'actionId'), type: str(raw?.type, 60), title: str(render.title ?? preview.title ?? raw?.title, 200), description: str(preview.description, 4000),
    targetLabel: str(raw?.targetLabel, 1000),
    status: pick(raw?.status === 'pending_approval' ? 'pending' : raw?.status, ACTION_STATUSES, 'pending'),
    error: raw?.error ? actionErrorMessage(raw.error.code ?? raw.error, raw.error.message) : '', createdAt: dateOf(raw?.createdAt)
  };
  if (raw?.source === 'mcp') action.source = 'mcp';
  if (typeof (raw?.family ?? render.family) === 'string') action.family = str(raw?.family ?? render.family, 20);
  if (raw?.expiresAt) action.expiresAt = dateOf(raw.expiresAt);
  const alerts = Array.isArray(raw?.alerts) ? raw.alerts : Array.isArray(render.alerts) ? render.alerts : null;
  if (alerts) action.alerts = alerts.slice(0, 30).map((alert: any) => ({ code: str(alert?.code ?? alert, 60), message: cleanText(alert?.message ?? alert?.text, 600) }));
  // Nested render (03-APPROBATION §4) or the flat card of the API (actions/views.js cardView).
  const effects = Array.isArray(render.effects) ? render.effects : Array.isArray(raw?.effects) ? raw.effects : null;
  if (effects) action.effects = effects.slice(0, 30).map((effect: unknown) => cleanText(effect, 300)).filter(Boolean);
  const irreversible = typeof render.irreversible === 'boolean' ? render.irreversible : raw?.irreversible;
  if (typeof irreversible === 'boolean') action.irreversible = irreversible;
  return action;
}
// The panel's action as the shared card shows it (agent-action-card, 06-FRONT §7): compact, gestures given by the panel.
export function actionCard(action: AgentAction, conversation: Pick<AgentConversation, 'source' | 'title'> | null | undefined): ApprovalAction {
  const author = conversationAuthor(conversation);
  const mcp = action.source === 'mcp' || Boolean(author);
  const render: RawRecord = { title: action.title || actionTypeLabel(action.type) };
  if (action.effects) render['effects'] = action.effects;
  if (action.irreversible !== undefined) render['irreversible'] = action.irreversible;
  return normalizeApprovalAction({
    id: action.id, type: action.type, status: action.status, family: action.family, source: mcp ? 'mcp' : 'agent',
    client: mcp ? { label: author?.badge || 'Claude', verified: author?.badge === 'Claude' } : undefined,
    createdAt: action.createdAt, expiresAt: action.expiresAt, targetLabel: action.targetLabel, alerts: action.alerts || [], render,
    error: action.error ? { message: action.error } : undefined
  });
}
export function normalizeChangeset(raw: RawRecord): AgentChangeset {
  const action = str(raw?.action ?? raw?.target?.action, 30);
  return {
    id: idOf(raw, 'changeSetId', 'changesetId'), kind: str(raw?.kind, 30), ...(action ? { action } : {}), summary: str(raw?.summary, 500),
    status: raw?.status === 'undone' ? 'undone' : 'applied', createdAt: dateOf(raw?.createdAt)
  };
}
export function normalizeEvent(raw: RawRecord): AgentEvent | null {
  const seq = Number(raw?.seq);
  if (!Number.isInteger(seq) || seq < 1 || typeof raw?.type !== 'string') return null;
  const data = raw.data && typeof raw.data === 'object' ? raw.data : {};
  return { seq, type: raw.type, runId: str(raw.runId ?? data.runId, 200), data, createdAt: dateOf(raw.createdAt) };
}

// ---- Monotonic merges: a late or replayed value never moves an entity backwards ------------

const ACTION_RANK: Record<AgentActionStatus, number> = {
  pending: 0, approved: 1, executing: 1, rejected: 2, executed: 2, partially_executed: 2, failed: 2, expired: 2, withdrawn: 2
};
const FILE_RANK: Record<AgentIngestionStatus, number> = { pending: 0, processing: 1, ready: 2, failed: 2 };
export const mergeAction = (current: AgentAction | undefined, next: AgentAction): AgentAction =>
  !current || ACTION_RANK[next.status] >= ACTION_RANK[current.status]
    ? { ...current, ...next, title: next.title || current?.title || '', description: next.description || current?.description || '', targetLabel: next.targetLabel || current?.targetLabel || '', type: next.type || current?.type || '',
      ...(next.alerts || current?.alerts ? { alerts: next.alerts || current?.alerts } : {}) }
    : current;
// Metadata from the latest value, status by rank (an upload response may arrive after file_ingested).
export function mergeFile(current: AgentFile | undefined, next: AgentFile): AgentFile {
  const older = Boolean(current && FILE_RANK[next.status] < FILE_RANK[current.status]);
  return {
    id: next.id, name: next.name || current?.name || 'Document', mimeType: next.mimeType || current?.mimeType || '', size: next.size || current?.size || 0,
    status: older ? current!.status : next.status, error: older ? current!.error : next.error
  };
}
export const mergeChangeset = (current: AgentChangeset | undefined, next: AgentChangeset): AgentChangeset =>
  current?.status === 'undone' ? current
    : { ...current, ...next, summary: next.summary || current?.summary || '', kind: next.kind || current?.kind || '', createdAt: current?.createdAt || next.createdAt };

export function upsert<T extends { id: string }>(list: T[], value: T, merge: (current: T | undefined, next: T) => T = (_, next) => next): T[] {
  if (!value.id) return list;
  const index = list.findIndex(item => item.id === value.id);
  if (index < 0) return [...list, value];
  const merged = merge(list[index], value);
  return merged === list[index] ? list : [...list.slice(0, index), merged, ...list.slice(index + 1)];
}
export const byDate = <T extends { createdAt: string }>(list: T[]) =>
  list.map((item, index) => ({ item, index })).sort((a, b) => (a.item.createdAt || '').localeCompare(b.item.createdAt || '') || a.index - b.index).map(entry => entry.item);

// ---- Labels -------------------------------------------------------------------------------

export function runStatusLabel(run: AgentRun | null | undefined): string {
  if (!run) return RUN_STATUS_LABELS.idle;
  if (run.cancelRequested && !isFinalRun(run)) return RUN_STATUS_LABELS.cancelling;
  return RUN_STATUS_LABELS[run.status] || RUN_STATUS_LABELS.idle;
}
// Status of a conversation in the history list (its last run); nothing before the first run.
export interface HistoryStatus { label: string; tone: 'active' | 'waiting' | 'failed' | 'muted'; }
const HISTORY_STATUS: Record<AgentRunStatus | 'cancelling', HistoryStatus> = Object.freeze({
  queued: { label: 'En cours', tone: 'active' }, running: { label: 'En cours', tone: 'active' }, cancelling: { label: 'Arrêt en cours', tone: 'active' },
  waiting_user: { label: 'Réponse attendue', tone: 'waiting' }, failed: { label: 'Non aboutie', tone: 'failed' },
  cancelled: { label: 'Arrêtée', tone: 'muted' }, completed: { label: 'Terminée', tone: 'muted' }
} as const);
export function historyStatus(run: { status: AgentRunStatus; cancelRequested?: boolean } | null | undefined): HistoryStatus | null {
  if (!run || !HISTORY_STATUS[run.status]) return null;
  return run.cancelRequested && !FINAL_RUN_STATUSES.includes(run.status) ? HISTORY_STATUS.cancelling : HISTORY_STATUS[run.status];
}
export function runErrorMessage(error: AgentRunError | null | undefined): string {
  if (!error) return 'La tâche n’a pas abouti. Vous pouvez reformuler ou relancer la demande.';
  if (/^LLM_/.test(error.code) && !RUN_ERROR_LABELS[error.code]) return 'Le service d’IA n’a pas pu traiter la demande. Réessayez ou reformulez-la.';
  return errorText(error, RUN_ERROR_LABELS, 'La tâche n’a pas abouti. Vous pouvez reformuler ou relancer la demande.');
}
export function fileErrorMessage(code: unknown, message?: unknown): string {
  return errorText({ code, message }, FILE_ERROR_LABELS, 'Lecture impossible.');
}
export function actionErrorMessage(code: unknown, message?: unknown): string {
  if (code === 'NOT_IMPLEMENTED') return 'Cette action n’est pas encore possible depuis l’agent. Effectuez-la directement dans Aglo.';
  return errorText({ code, message }, {}, 'L’action n’a pas pu être effectuée.');
}
// Server labels that say nothing to the user (older agent-server versions) fall back to the front label.
const GENERIC_TOOL_LABELS = ['Outil', 'Exécution d’un script'];
export function stepLabel(step: { tool: string; label?: string }): string {
  const label = cleanText(step.label || '').replace(/'/g, '’');
  return label && !GENERIC_TOOL_LABELS.includes(label) && label.length <= 160 ? label : TOOL_LABELS[step.tool] || 'Traitement de votre demande';
}
export const actionTypeLabel = (type: string) => ACTION_TYPE_LABELS[type] || 'Action à valider';
// A 'plan' changeset is either AI measurements to review, plans attached from a PDF (action 'attach', SPEC §3.5) or
// AI proposals withdrawn by the agent itself (action 'reject', SPEC §3.6: nothing to review, undoable).
const PLAN_ACTION_LABELS: Record<string, string> = { attach: 'Plans', reject: 'Métrés retirés' };
export const changesetKindLabel = (kind: string, action?: string) =>
  (kind === 'plan' && action && PLAN_ACTION_LABELS[action]) || CHANGESET_KIND_LABELS[kind] || 'Modification';
export const changesetNeedsReview = (change: Pick<AgentChangeset, 'kind' | 'action' | 'status'>) =>
  change.kind === 'plan' && !(change.action && PLAN_ACTION_LABELS[change.action]) && change.status === 'applied';

// ---- Follow mode: event targets, activity of the run (SPEC §8 `target`, follow mode) -------

// Identifiers carried by changeset_applied / measurement_proposed / changeset_undone / tool_started (SPEC §8). All optional:
// lotIds absent = unknown (the pages refresh every lot), lots = first touched lots with their title (bubble only).
export interface AgentTarget { projectId?: string; dpgfId?: string; lotIds?: string[]; lots?: { id: string; title: string }[]; postIds?: string[]; removedLotIds?: string[]; ops?: string[]; planId?: string; planIds?: string[]; folderId?: string; libraryPostId?: string; }
export const TARGET_LIMITS = Object.freeze({ lotIds: 200, lots: 10, lotTitleChars: 80, postIds: 50, removedLotIds: 200, ops: 20, planIds: 50 });
const OBJECT_ID = /^[a-f\d]{24}$/i;
const LEGACY_ID = /^[\w.-]{1,120}$/; // DPGF lot / section / post ids: lot_x, sous_lot_x, poste_x, sous-poste_x and older formats
const OP_NAME = /^[A-Za-z]{1,40}$/;
const ids = (value: unknown, pattern: RegExp, max: number) =>
  [...new Set((Array.isArray(value) ? value.slice(0, max * 4) : []).filter((id): id is string => typeof id === 'string' && pattern.test(id)))].slice(0, max);
// Hostile or malformed values are dropped field by field; a lotIds list with an invalid entry becomes « unknown ».
export function normalizeTarget(raw: unknown): AgentTarget {
  const source: RawRecord = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as RawRecord : {};
  const target: AgentTarget = {};
  for (const key of ['projectId', 'dpgfId', 'planId', 'folderId', 'libraryPostId'] as const) {
    const id = source[key];
    if (typeof id === 'string' && OBJECT_ID.test(id)) target[key] = id;
  }
  if (Array.isArray(source.lotIds) && source.lotIds.length <= TARGET_LIMITS.lotIds && source.lotIds.every((id: unknown) => typeof id === 'string' && LEGACY_ID.test(id))) {
    target.lotIds = ids(source.lotIds, LEGACY_ID, TARGET_LIMITS.lotIds);
  }
  if (Array.isArray(source.lots)) {
    const lots = new Map<string, { id: string; title: string }>();
    for (const lot of source.lots.slice(0, TARGET_LIMITS.lots * 4)) {
      if (lot && typeof lot.id === 'string' && LEGACY_ID.test(lot.id) && !lots.has(lot.id)) lots.set(lot.id, { id: lot.id, title: cleanText(lot.title, TARGET_LIMITS.lotTitleChars) });
    }
    target.lots = [...lots.values()].slice(0, TARGET_LIMITS.lots);
  }
  if (Array.isArray(source.postIds)) target.postIds = ids(source.postIds, LEGACY_ID, TARGET_LIMITS.postIds);
  if (Array.isArray(source.removedLotIds)) target.removedLotIds = ids(source.removedLotIds, LEGACY_ID, TARGET_LIMITS.removedLotIds);
  if (Array.isArray(source.ops)) target.ops = ids(source.ops, OP_NAME, TARGET_LIMITS.ops);
  if (Array.isArray(source.planIds)) target.planIds = ids(source.planIds, OBJECT_ID, TARGET_LIMITS.planIds);
  return target;
}
export const isObjectId = (value: unknown): value is string => typeof value === 'string' && OBJECT_ID.test(value);
export const isLotId = (value: unknown): value is string => typeof value === 'string' && LEGACY_ID.test(value);
// Fresh event handed by the panel state to the follow engine (seq > followFloor, each event once). run: the run as of that
// event, tracked in seq order; scope / projectTitle: the conversation before the event; owned: this tab follows the run;
// knownKind: kind of an undone changeset listed by the panel (older changeset_undone events carry no kind).
export interface AgentFollowSignal {
  event: AgentEvent; run: { id: string; status: AgentRunStatus } | null; conversationId: string; scope: AgentScope; projectTitle: string; owned: boolean; knownKind?: string;
}

// « Dernière action » of the bubble: the changeset summary without the « DPGF : » prefix written by the agent server,
// preceded by the lot title when the change touched one lot (« Gros œuvre : 12 postes ajoutés »).
export function lastActionLabel(kind: string, summary: unknown, target: AgentTarget = {}): string {
  const text = cleanText(summary).replace(/^DPGF\s*:\s*/i, '');
  const lot = kind === 'dpgf' && target.lots?.length === 1 && (!target.lotIds || target.lotIds.length <= 1) ? target.lots[0].title : '';
  const label = !text ? changesetKindLabel(kind) : lot ? `${lot} : ${text}` : text.charAt(0).toUpperCase() + text.slice(1);
  return label.length > 200 ? `${label.slice(0, 199)}…` : label;
}

// Phase of the run shown by the activity bubble (critic-ux microcopy).
export type AgentPhase = 'starting' | 'analysing' | 'working' | 'thinking' | 'waiting' | 'approval' | 'stopping' | 'done' | 'failed' | 'cancelled';
export const PHASE_LABELS: Record<AgentPhase, string> = {
  starting: 'Démarrage…', analysing: 'Analyse de votre demande…', working: 'Mise à jour de la DPGF…', thinking: 'Prépare l’étape suivante…',
  waiting: 'L’IA attend votre réponse', approval: 'Une action attend votre validation', stopping: 'Arrêt en cours…', done: 'Terminé',
  failed: 'La tâche n’a pas abouti. Les modifications déjà enregistrées sont conservées.', cancelled: 'Arrêté. Les modifications déjà enregistrées sont conservées.'
};
// since: start of the current phase / step (ms), for the elapsed time; live: the run was seen active in this tab.
export interface AgentActivity { runId: string; phase: AgentPhase; now: string; last: string; since: number; live: boolean; }
export function activityPhase(run: AgentRun | null | undefined, steps: AgentStep[], pendingActions: number, stopping: boolean): AgentPhase | null {
  if (!run) return pendingActions ? 'approval' : null;
  if (stopping && !isFinalRun(run)) return 'stopping';
  if (run.status === 'waiting_user') return 'waiting';
  if (run.status === 'queued') return 'starting';
  if (run.status === 'running') return !steps.length ? 'analysing' : steps[steps.length - 1].status === 'running' ? 'working' : 'thinking';
  if (pendingActions) return 'approval';
  return run.status === 'completed' ? 'done' : run.status;
}
// Seconds then minutes (« 45 s », « 2 min 05 »), for the bubble.
export function elapsedLabel(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${String(seconds % 60).padStart(2, '0')}`;
}

// ---- Errors -------------------------------------------------------------------------------

export type AgentErrorContext = 'load' | 'list' | 'send' | 'upload' | 'cancel' | 'decide' | 'undo' | 'download';
// Angular HttpErrorResponse: { status, error: { error: { code, message } } }.
export function errorCode(error: any): string {
  const body = error?.error;
  return str(body?.error?.code ?? body?.code ?? error?.code, 60);
}
export function friendlyError(error: any, context: AgentErrorContext = 'send'): string {
  const status = typeof error?.status === 'number' ? error.status : undefined, code = errorCode(error);
  if (status === 0) return 'Connexion interrompue. Votre brouillon est conservé ; vous pouvez réessayer.';
  if (status === 401 || code === 'UNAUTHORIZED') return 'Votre session a expiré. Reconnectez-vous pour poursuivre.';
  if (status === 403 || code === 'FORBIDDEN') return context === 'send' || context === 'load'
    ? 'Vous n’avez pas accès à cette conversation ou au projet qui lui est lié.' : 'Vous n’avez pas les droits nécessaires pour cette opération.';
  if (code === 'RUN_IN_PROGRESS') return 'Une tâche est déjà en cours dans cette conversation. Attendez sa fin ou arrêtez-la.';
  if (code === 'QUOTA_EXCEEDED' || status === 429) return 'La limite d’utilisation de l’agent est atteinte pour votre agence. Réessayez plus tard.';
  if (code === 'NOT_IMPLEMENTED' || status === 501) return 'Cette action n’est pas encore possible depuis l’agent. Effectuez-la directement dans Aglo.';
  if (status === 404 || code === 'NOT_FOUND') {
    if (context === 'undo') return 'Cette modification est introuvable.';
    if (context === 'decide') return 'Cette action est introuvable.';
    if (context === 'download') return 'Ce document n’est plus disponible.';
    return 'Cette conversation n’est plus disponible.';
  }
  if (status === 409 || code === 'CONFLICT') {
    if (context === 'undo') return 'Annulation impossible : les éléments concernés ont été modifiés depuis. Vérifiez-les directement dans Aglo.';
    if (context === 'decide') return 'Cette action a déjà été traitée.';
    if (context === 'cancel') return 'Cette tâche est déjà terminée.';
    return 'La conversation a évolué. Actualisez-la avant de poursuivre.';
  }
  if (status === 413 || code === 'TOO_LARGE') return context === 'upload'
    ? `Fichier trop volumineux : ${AGENT_LIMITS.fileBytes / 1024 / 1024} Mo maximum par fichier, ${AGENT_LIMITS.filesPerUpload} fichiers par envoi.`
    : 'Le contenu dépasse la taille autorisée.';
  if (status === 400 || status === 415 || status === 422 || code === 'VALIDATION' || code === 'UNPROCESSABLE') return context === 'upload'
    ? 'Ce fichier ne peut pas être ajouté. Formats acceptés : PDF, PNG, JPG, DOCX, XLSX, CSV et TXT.'
    : 'Cette demande ne peut pas être traitée. Vérifiez son contenu.';
  if (typeof status === 'number' && status >= 500) return 'Le service est momentanément indisponible. Réessayez dans un instant.';
  if (context === 'list' || context === 'load') return 'Impossible de charger la conversation. Réessayez dans un instant.';
  return 'La demande n’a pas abouti. Votre brouillon est conservé ; vous pouvez réessayer.';
}
// Polling stops on these: retrying cannot succeed without a user action.
export const isFatalError = (error: any) => [401, 403, 404].includes(error?.status);

// ---- Uploads ------------------------------------------------------------------------------

export function uploadProblem(files: { name: string; size: number }[], alreadyInConversation: number): string {
  if (files.length > AGENT_LIMITS.filesPerUpload) return `Ajoutez au plus ${AGENT_LIMITS.filesPerUpload} fichiers à la fois.`;
  if (files.length + alreadyInConversation > AGENT_LIMITS.filesPerConversation) return `Une conversation accepte au plus ${AGENT_LIMITS.filesPerConversation} fichiers. Ouvrez une nouvelle conversation.`;
  const extension = (name: string) => { const match = /\.[^.]+$/.exec(name || ''); return match ? match[0].toLowerCase() : ''; };
  if (files.some(file => !AGENT_UPLOAD_EXTENSIONS.includes(extension(file.name)))) return 'Formats acceptés : PDF, PNG, JPG, DOCX, XLSX, CSV et TXT.';
  if (files.some(file => file.size > AGENT_LIMITS.fileBytes)) return `Chaque fichier doit faire au plus ${AGENT_LIMITS.fileBytes / 1024 / 1024} Mo.`;
  if (files.some(file => file.size === 0)) return 'Un des fichiers est vide.';
  return '';
}

// AI measurement proposals awaiting review (SPEC §3.6), for the plan measurement editor.
export const needsAiReview = (measurement: { metadata?: { source?: string; needsReview?: boolean } | null } | null | undefined) =>
  measurement?.metadata?.source === 'ai' && measurement.metadata.needsReview === true;

// ---- Route context, links, support widget (unchanged behaviour from v1) -------------------

export function routeContext(params: Record<string, unknown>): AgentContext {
  const valid = (value: unknown): value is string => typeof value === 'string' && /^[a-f\d]{24}$/i.test(value);
  const context: AgentContext = {};
  const project = params.projectId || params.ceProjectId;
  if (valid(project)) context.projectId = project;
  if (valid(params.dpgfId)) context.dpgfId = params.dpgfId;
  if (valid(params.planId)) context.planId = params.planId;
  return context;
}

export function hideSupportWidget(api?: { isChatHidden?(): boolean; hideWidget?(): void; showWidget?(): void }): () => void {
  if (!api?.hideWidget || !api.showWidget || api.isChatHidden?.() !== false) return () => {};
  api.hideWidget();
  let restored = false;
  return () => { if (!restored) { restored = true; api.showWidget!(); } };
}

export function safeHref(target: string): boolean {
  if (/[\\\u0000- ]/.test(target)) return false;
  if (/^\/architect(?:\/|$)/.test(target)) return true;
  try { const url = new URL(target); return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password; } catch { return false; }
}

// Only HTTP(S) and architect pages become links; any model HTML stays plain text.
export function messageParts(text: string): { text: string; href?: string }[] {
  const pattern = /\[([^\]\n]{1,300})\]\(([^\s)]+)\)|(https?:\/\/[^\s<>]+)/g;
  const parts: { text: string; href?: string }[] = [];
  let start = 0, match: RegExpExecArray | null;
  while ((match = pattern.exec(text || '')) !== null) {
    if (match.index > start) parts.push({ text: text.slice(start, match.index) });
    const label = match[1] || match[3], target = match[2] || match[3];
    parts.push(safeHref(target) ? { text: label, href: target } : { text: match[0] });
    start = pattern.lastIndex;
  }
  if (start < (text || '').length) parts.push({ text: text.slice(start) });
  return parts;
}
